import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  :root { --aqk-navy:#0A1931; --aqk-blue:#1A3A6E; --aqk-gold:#C9A84C; --aqk-gold-lt:#E8C97A; --aqk-light:#F7F9FC; --aqk-gray:#6B7A99; --aqk-gray-lt:#EEF2F8; }
  .aqk-contact * { box-sizing: border-box; }
  .aqk-contact { font-family: 'DM Sans', sans-serif; background: var(--aqk-light); color: var(--aqk-navy); min-height: 100vh; }

  /* HERO */
  .aqk-contact-hero {
    background: linear-gradient(135deg, var(--aqk-navy) 0%, #112550 100%);
    padding: 80px 40px 110px;
    position: relative; overflow: hidden;
    text-align: center;
  }
  .aqk-contact-hero::before {
    content: '';
    position: absolute; inset: 0;
    background-image:
      linear-gradient(rgba(201,168,76,0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(201,168,76,0.05) 1px, transparent 1px);
    background-size: 56px 56px;
  }
  .aqk-contact-hero::after {
    content: '';
    position: absolute; bottom:-2px; left:0; right:0;
    height: 60px; background: var(--aqk-light);
    clip-path: ellipse(55% 100% at 50% 100%);
  }
  .aqk-contact-hero-inner { position: relative; z-index: 1; }
  .aqk-contact-hero-label {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 11px; font-weight: 600; letter-spacing: 3px;
    text-transform: uppercase; color: var(--aqk-gold); margin-bottom: 16px;
    justify-content: center;
  }
  .aqk-contact-hero-label::before { content:''; width:24px; height:2px; background:var(--aqk-gold); }
  .aqk-contact-hero h1 {
    font-family: 'Playfair Display', serif;
    font-size: clamp(36px, 5vw, 58px); font-weight: 900;
    color: #fff; margin-bottom: 14px; line-height: 1.1;
  }
  .aqk-contact-hero h1 span { color: var(--aqk-gold); }
  .aqk-contact-hero-sub { font-size: 17px; color: #ffffff; max-width: 480px; margin: 0 auto; line-height: 1.7; }

  /* QUICK INFO CARDS */
  .aqk-contact-info-strip {
    max-width: 1100px; margin: 0 auto;
    padding: 0 40px; margin-top: -36px;
    position: relative; z-index: 2;
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }
  @media (max-width: 768px) { .aqk-contact-info-strip { grid-template-columns: 1fr; padding: 0 20px; } }
  .aqk-contact-info-card {
    background-color: #ffffff;
    background-image: none;
    border-radius: 16px;
    padding: 26px 26px;
    box-shadow: 0 8px 40px rgba(10,25,49,0.1);
    border: 1.5px solid var(--aqk-blue);
    transition: transform .2s;
  }
  .aqk-contact-info-card:hover { transform: translateY(-4px); }
  .aqk-contact-info-card .aqk-contact-info-title { font-family:'Playfair Display',serif; font-size: 16px; font-weight: 700; color: #0A1931; margin-bottom: 6px; }
  .aqk-contact-info-card .aqk-contact-info-value { font-size: 14px; color: #4A5670; line-height: 1.5; }
  .aqk-contact-info-card .aqk-contact-info-value a { color: #1A3A6E; text-decoration: none; font-weight: 600; }
  .aqk-contact-info-card .aqk-contact-info-value a:hover { color: var(--aqk-gold); }

  /* MAIN BODY */
  .aqk-contact-body { max-width: 1100px; margin: 0 auto; padding: 60px 40px 100px; }
  @media (max-width: 768px) { .aqk-contact-body { padding: 40px 20px 80px; } }
  .aqk-contact-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-bottom: 56px;
  }
  @media (max-width: 900px) { .aqk-contact-grid { grid-template-columns: 1fr; } }

  /* SEC LABEL */
  .aqk-contact-sec-label {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 11px; font-weight: 600; letter-spacing: 3px;
    text-transform: uppercase; color: var(--aqk-gold); margin-bottom: 8px;
  }
  .aqk-contact-sec-label::before { content:''; width:20px; height:2px; background:var(--aqk-gold); }
  .aqk-contact-sec-title {
    font-family: 'Playfair Display', serif;
    font-size: clamp(22px, 3vw, 30px); font-weight: 700;
    color: var(--aqk-navy); margin-bottom: 24px;
  }

  /* FORM */
  .aqk-contact-form-wrap { background: #fff; border-radius: 20px; padding: 40px; box-shadow: 0 4px 32px rgba(10,25,49,0.07); }
  .aqk-contact-field { margin-bottom: 20px; }
  .aqk-contact-label { display: block; font-size: 13px; font-weight: 600; color: var(--aqk-navy); margin-bottom: 6px; letter-spacing: 0.3px; }
  .aqk-contact-input, .aqk-contact-textarea {
    width: 100%; padding: 13px 16px;
    border: 1.5px solid var(--aqk-gray-lt);
    border-radius: 10px;
    font-size: 14px; font-family: 'DM Sans', sans-serif;
    color: var(--aqk-navy); background: var(--aqk-light);
    transition: border-color .2s, box-shadow .2s;
    outline: none;
  }
  .aqk-contact-input:focus, .aqk-contact-textarea:focus {
    border-color: var(--aqk-gold);
    box-shadow: 0 0 0 3px rgba(201,168,76,0.15);
    background: #fff;
  }
  .aqk-contact-textarea { resize: vertical; min-height: 130px; }
  .aqk-contact-submit {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--aqk-navy); color: #fff;
    font-weight: 700; font-size: 15px;
    padding: 15px 36px; border-radius: 100px;
    border: none; cursor: pointer;
    transition: background .2s, transform .2s;
    font-family: 'DM Sans', sans-serif;
  }
  .aqk-contact-submit:hover { background: var(--aqk-blue); transform: translateY(-2px); }

  /* MAP */
  .aqk-contact-map-wrap { border-radius: 20px; overflow: hidden; box-shadow: 0 4px 32px rgba(10,25,49,0.1); height: 100%; min-height: 360px; }
  .aqk-contact-map-wrap iframe { width: 100%; height: 100%; min-height: 360px; display: block; border: none; }

  /* HOURS */
  .aqk-contact-hours {
    background: linear-gradient(135deg, var(--aqk-navy), #112550);
    border-radius: 20px; padding: 40px;
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px;
    margin-bottom: 56px;
  }
  @media (max-width: 768px) { .aqk-contact-hours { grid-template-columns: 1fr; } }
  .aqk-contact-hour-item { text-align: center; }
  .aqk-contact-hour-day { font-size: 12px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--aqk-gold); margin-bottom: 8px; }
  .aqk-contact-hour-time { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 700; color: #fff; }
  .aqk-contact-hour-status { font-size: 12px; color: rgba(255,255,255,0.65); margin-top: 4px; }

  /* CONTACT DIRECTORY */
  .aqk-contact-directory {
    background: #fff;
    border-radius: 20px;
    padding: 40px;
    box-shadow: 0 4px 32px rgba(10,25,49,0.07);
    margin-bottom: 56px;
  }
  .aqk-contact-directory-address {
    font-size: 14px; color: var(--aqk-gray); margin-bottom: 28px;
    padding-bottom: 24px; border-bottom: 1px solid var(--aqk-gray-lt);
  }
  .aqk-contact-directory-address strong { color: var(--aqk-navy); font-family: 'Playfair Display', serif; font-size: 17px; display: block; margin-bottom: 4px; }
  .aqk-contact-directory-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px 32px;
  }
  @media (max-width: 900px) { .aqk-contact-directory-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .aqk-contact-directory-grid { grid-template-columns: 1fr; } }
  .aqk-contact-dept-name {
    font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--aqk-gold); margin-bottom: 8px;
    padding-bottom: 8px; border-bottom: 2px solid var(--aqk-gray-lt);
  }
  .aqk-contact-dept-number {
    font-size: 15px; font-weight: 600; color: var(--aqk-navy); margin-bottom: 4px;
  }
  .aqk-contact-dept-number a { color: var(--aqk-navy); text-decoration: none; }
  .aqk-contact-dept-number a:hover { color: var(--aqk-blue); }
`;

export default function ContactUs() {
  const [loading, setLoading] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const formData = new FormData(event.target);
    formData.append("access_key", "cf26476a-73fa-42fc-ac54-9de6906e4a3d");
    const json = JSON.stringify(Object.fromEntries(formData));
    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: json,
    }).then(r => r.json());
    setLoading(false);
    if (res.success) {
      Swal.fire({ title: "Message Sent!", text: "We'll get back to you soon.", icon: "success", confirmButtonColor: "#C9A84C" });
      event.target.reset();
    }
  };

  const directory = [
    { dept: "Admission Office", numbers: ["03165994941"] },
    { dept: "Admin Office", numbers: ["03345472670", "03185816927"] },
    { dept: "Accounts Office", numbers: ["03453300699"] },
    { dept: "Dr. A. Q Khan Academy", numbers: ["03140421145"] },
    { dept: "Principal's Number", numbers: ["03340901415"] },
    { dept: "Complaint Cell", numbers: ["03365142887"] },
  ];

  return (
    <>
      <style>{css}</style>
      <div className="aqk-contact">

        {/* HERO */}
        <div className="aqk-contact-hero">
          <div className="aqk-contact-hero-inner">
            <div className="aqk-contact-hero-label">Get in Touch</div>
            <h1>We'd Love to <span>Hear</span> From You</h1>
            <p className="aqk-contact-hero-sub">
              Have a question about admissions, programs, or campus life? Reach out and our team will be happy to help.
            </p>
          </div>
        </div>

        {/* INFO STRIP */}
        <div className="aqk-contact-info-strip">
          <div className="aqk-contact-info-card">
            <div className="aqk-contact-info-title">Call Us</div>
            <div className="aqk-contact-info-value"><a href="tel:+923453300699">+92 345 3300699</a></div>
            <div className="aqk-contact-info-value" style={{ marginTop: 2 }}>Mon–Sat during office hours</div>
          </div>
          <div className="aqk-contact-info-card">
            <div className="aqk-contact-info-title">Email Us</div>
            <div className="aqk-contact-info-value"><a href="mailto:aqkhanedu@gmail.com">aqkhanedu@gmail.com</a></div>
            <div className="aqk-contact-info-value" style={{ marginTop: 2 }}>We reply within 24 hours</div>
          </div>
          <div className="aqk-contact-info-card">
            <div className="aqk-contact-info-title">Visit Us</div>
            <div className="aqk-contact-info-value">3 Harley Street, Rawalpindi</div>
            <div className="aqk-contact-info-value" style={{ marginTop: 2 }}>Punjab, Pakistan</div>
          </div>
        </div>

        {/* BODY */}
        <div className="aqk-contact-body">

          {/* Form + Map */}
          <div className="aqk-contact-grid">
            <div>
              <div className="aqk-contact-sec-label">Send a Message</div>
              <h2 className="aqk-contact-sec-title">Contact Form</h2>
              <div className="aqk-contact-form-wrap">
                <form onSubmit={onSubmit}>
                  <div className="aqk-contact-field">
                    <label className="aqk-contact-label" htmlFor="name">Full Name</label>
                    <input className="aqk-contact-input" type="text" id="name" name="name" placeholder="Your full name" required />
                  </div>
                  <div className="aqk-contact-field">
                    <label className="aqk-contact-label" htmlFor="email">Email Address</label>
                    <input className="aqk-contact-input" type="email" id="email" name="email" placeholder="your@email.com" required />
                  </div>
                  <div className="aqk-contact-field">
                    <label className="aqk-contact-label" htmlFor="message">Message</label>
                    <textarea className="aqk-contact-textarea" id="message" name="message" placeholder="How can we help you?" required />
                  </div>
                  <button type="submit" className="aqk-contact-submit" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            <div>
              <div className="aqk-contact-sec-label">Find Us</div>
              <h2 className="aqk-contact-sec-title">Our Location</h2>
              <div className="aqk-contact-map-wrap">
                <iframe
                  src="https://maps.google.com/maps?width=100%25&height=500&hl=en&q=3%20Harley%20Street,%20Rawalpindi,%20Pakistan+(Dr%20AQ%20Khan%20College%20)&t=&z=14&ie=UTF8&iwloc=B&output=embed"
                  title="Google Maps — Dr AQ Khan College"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* Contact Directory */}
          <div className="aqk-contact-sec-label">Contact Directory</div>
          <h2 className="aqk-contact-sec-title">Reach the Right Department</h2>
          <div className="aqk-contact-directory">
            <div className="aqk-contact-directory-address">
              <strong>Dr. A. Q Khan College</strong>
              Harley Street, Rawalpindi
            </div>
            <div className="aqk-contact-directory-grid">
              {directory.map((d) => (
                <div key={d.dept}>
                  <div className="aqk-contact-dept-name">{d.dept}</div>
                  {d.numbers.map((n) => (
                    <div className="aqk-contact-dept-number" key={n}>
                      <a href={`tel:+92${n.slice(1)}`}>{n}</a>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Hours */}
          <div className="aqk-contact-sec-label">Opening Hours</div>
          <h2 className="aqk-contact-sec-title">When We're Available</h2>
          <div className="aqk-contact-hours">
            <div className="aqk-contact-hour-item">
              <div className="aqk-contact-hour-day">Monday – Friday</div>
              <div className="aqk-contact-hour-time">8:00 am – 4:00 pm</div>
              <div className="aqk-contact-hour-status">Office & Admissions open</div>
            </div>
            <div className="aqk-contact-hour-item">
              <div className="aqk-contact-hour-day">Saturday</div>
              <div className="aqk-contact-hour-time">9:00 am – 1:00 pm</div>
              <div className="aqk-contact-hour-status">Limited services</div>
            </div>
            <div className="aqk-contact-hour-item">
              <div className="aqk-contact-hour-day">Sunday</div>
              <div className="aqk-contact-hour-time">Closed</div>
              <div className="aqk-contact-hour-status">Public holiday</div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}